import React, { useState, useRef, useEffect } from 'react';
import {
  Typography,
  Icon,
  Button,
  AppBar,
  IconButton,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import { imageStyles, useStyles } from './ReportAssessmentPhotos.styles';
import { get, size, set, map, uniqBy } from 'global/lib/lodash';
import ImageGrid from 'global/components/image-grid';
import { useScreen } from 'global/styles';
import { getBase64Image, sortAlphaNum } from 'global/utils';
import sendData from 'redux/actions/sendData';
import Confirm from 'global/components/confirm';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Alert } from 'global/components/alerts';
import useRetrieveData from 'global/hooks/useRetrieveData';
import DropArea from 'global/components/drop-area';
import FolderIcon from '@mui/icons-material/Folder';
import { useSupersets } from 'global/hooks/useSupersets';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined';
import { awaitAll } from 'global/utils/awaitAll';
import { PhotosGrid } from 'global/components/perform-grid';
import { UploadFiles } from '../../../redux/actions/imageUploader';
import { Media } from '../../../global/components/Media';

const PhotoIncludes = ['supersets', 'realm', 'assessment', 'images'];

const UploadImage = (props) => {
  const { file, actions } = props;

  const isGood = file.quality == 1;
  const isNeg = file.quality == 0;

  const classes = imageStyles();

  const selectPos = () => {
    actions.updateImage({ ...file, quality: 1 })
  };

  const selectNeg = () => {
    actions.updateImage({ ...file, quality: 0 })
  };

  const selectDel = (e) => {
    actions.onDeleteFile && actions.onDeleteFile(file);
  };

  const onValuesChange = o => {
    actions.updateImage({ ...file, ...o.values });
  }

  return (
    <div className={classes.root} onClick={e => e.stopPropagation()}>
      <Media {...file} assessment={props.assessment} realmId={file.location || props.location} opt={'tag'} onValuesChange={onValuesChange} />
      <Button onClick={selectDel} size="small" className={classes.deleteBtn}>
        <Icon>cancel</Icon>
      </Button>
      <div className={classes.rankContainer}>
        <Button onClick={selectPos} className={classes.posRank}>
          {isGood ? <ThumbUp /> : <ThumbUpOutlined />}
        </Button>
        <Button onClick={selectNeg} className={classes.negRank}>
          {isNeg ? <ThumbDown /> : <ThumbDownOutlined />}
        </Button>
      </div>
    </div>
  );
};

function ReportAssessmentPhotos(props) {
  const { assessment, assessmentId, userCanEdit } = props;
  const include = PhotoIncludes.join(',');
  const urlParams = { filter: { assessment_id: assessmentId }, include: size(PhotoIncludes) ? include : undefined };

  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });

  const [uploadModal, setUploadModal] = useState(false);
  const [folders, setFolders] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [location, setLocation] = useState();
  const [superset, setSuperset] = useState();
  const [alerts, setAlerts] = useState();
  const [uploadStats, setUploadStats] = useState({ isUploading: false, progress: 0 });
  const [dlStats, setDlStats] = useState();
  const [confirm, setConfirm] = useState();
  const dropRef = useRef(null);
  const { data, loading: _load, update } = useRetrieveData({ url: '/realm-photos' }, { params: urlParams, build: ['realmPhotos'] });
  const { supersets } = useSupersets({ assessment: assessment.data });
  const assessmentRealmId = get(assessment, 'data.realm.id');

  const loading = _load || assessment.loading;

  const updateAll = async () => {
    await Promise.all([
      assessment.update(),
      update(),
    ])
  };

  useEffect(() => {
    if (data && !loading) {
      const folds = {};
      const realmPhotos = data;
      realmPhotos.forEach((rp, i) => {
        const realmid = get(rp, 'realm.id');
        const photos = get(folds, [realmid, 'photos'], []);
        set(folds, realmid, {
          id: `realm-${get(rp, 'realm.id')}`,
          name: get(rp, 'name'),
          photos: [...photos, { ...rp }].map(r => ({ ...get(r, 'images.0', {}), ...r, assessment: assessment.data })).sort(sortAlphaNum),
          realm: get(rp, 'realm'),
          icon: FolderIcon,
        });
      });
      const fols = map(folds, (o) => o).sort((a, b) => {
        // Show the Assessment's realm_id as first folder
        if (get(a, 'realm.id') === assessmentRealmId) {
          return -1;
        } else if (get(b, 'realm.id') === assessmentRealmId) {
          return 1;
        }
        // If it's not the Assessment's realm_id, order folders alphabetically
        const A = get(a, 'realm.name');
        const B = get(b, 'realm.name');
        return A === B ? 0 : A > B ? 1 : -1;
      });
      setFolders(fols);
    }
  }, [data, loading]);

  useEffect(() => {
    setLocation(assessmentRealmId);
  }, [assessmentRealmId]);

  const uploadPhotos = () => {
    setUploadModal(true);
  };

  const confirmDownload = async () => {
    setDlStats((dl) => ({
      ...dl,
      isLoading: true,
      progress: 0,
    }));
    setConfirm();

    let totalImages = 0;
    folders.map((f) => (totalImages += f.photos.length));
    const folds = [];
    const zip = new JSZip();
    const zipFolders = {};
    await Promise.all(
      folders.map(async (f) => {
        try {
          folds.push({ ...f });
          const folderName = get(f, 'realm.name');
          set(zipFolders, folderName, zip.folder(folderName));
          if (f.photos) {
            await Promise.all(
              f.photos.map(async (_f) => {
                const data = await getBase64Image(_f.large, true);
                setDlStats((dl) => ({
                  ...dl,
                  progress: dl.progress + 100 / totalImages,
                }));
                folds.push({
                  ..._f,
                  folderName,
                  data,
                });
              })
            );
          }
        } catch (err) {
          setDlStats((dl) => ({
            ...dl,
            progress: dl.progress + 100 / totalImages.length,
          }));
          return null;
        }
      })
    );

    folds
      .filter((f) => f)
      .forEach((file) => {
        if (file.data) {
          get(zipFolders, file.folderName).file(file.name, file.data, { base64: true });
        }
      });

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${get(assessment, 'data.name')}-photos.zip`);
      setDlStats();
    });
  };

  const download = () => {
    setConfirm({
      title: 'Download All Images?',
      content: `Are you sure you want to download all images?`,
      onOk: () => confirmDownload(),
      onCancel: () => setConfirm(),
    });
  };

  const onDropChange = (files) => {
    setUploadFiles(files);
  };

  const onDeleteFile = (file) => {
    setUploadFiles((uf) => uf.filter((uf) => uf.id !== file.id));
  };

  const updateImage = file => {
    setUploadFiles((uf) =>
      uf.map((f) => {
        if (f.id === file.id) {
          f = file;
        }
        return f;
      })
    );
  }

  const onLocationChange = (loc) => {
    setLocation(loc.target.value);
  };

  const onSupersetChange = (loc) => {
    setSuperset(loc.target.value);
  };

  const uploadSuccess = () => {
    setAlerts({
      message: 'Upload success',
      severity: 'success',
    });
    clearProgress();
    updateAll();
    setUploadModal(false);
    setUploadFiles([]);
  };

  const uploadFail = (failed) => {
    let message = failed.map((f) => f.message || '').join(', ');
    clearProgress();
    setAlerts({
      message: (
        <>
          <p>{`${failed.length} Upload failed: ${message}`}</p>
          <p>{`These files have been placed in the upload window. Please try again.`}</p>
        </>
      ),
      severity: 'error',
    });
    setUploadFiles(failed.map((f) => f.file));
  };

  const clearProgress = () => {
    setUploadStats((upl) => ({
      ...upl,
      isUploading: false,
      progress: 0,
    }));
  };

  const cancelUpload = () => {
    setUploadStats((upl) => ({
      ...upl,
      isUploading: false,
      progress: 0,
    }));
    setUploadFiles([]);
  };

  const sendFile = async (file) => {
    try {
      await UploadFiles(file, { batchIndex: get(file, 'data.attributes.batchIndex'), batchSize: get(file, 'data.attributes.batchSize')});
      return await sendData(
        {
          url: '/realm-photos',
          method: 'post',
          data: { ...file },
        },
        { maxTries: 3 }
      );
    } catch (err) {
      console.error(err);
      throw new Error(err.message);
    }
  };

  const submitFiles = async () => {
    setUploadStats((upl) => ({
      ...upl,
      isUploading: true,
    }));

    const failed = [];

    const files = uploadFiles.map((file, index) => {
      const relationships = !!size(file.supersets) || !!superset
        ? {
            supersets: {
              data: file.supersets ? file.supersets.map(s => ({ type: 'supersets', id: s })) : [{ type: 'supersets', id: superset }],
            }
          }
        : undefined;


      return {
        file,
        data: {
          type: 'realm-photos',
          attributes: {
            name: file.file.name,
            realm_id: file.location || location,
            assessment_id: assessmentId,
            quality: file.quality,
            images: [file],

            batchSize: size(uploadFiles),
            batchIndex: index,
          },
          relationships,
        },
      };
    });

    await awaitAll(
      files.map((f) => () => async () => {
        try {
          return await sendFile({ data: f.data });
        } catch (err) {
          failed.push({ ...f, message: err.message });
        }
      }),
      {
        queueSize: 5,
        onProgress: (p) => {
          setUploadStats((upl) => ({
            ...upl,
            progress: p * 100,
          }));
        },
      }
    );

    if (failed.length > 0) {
      uploadFail(failed);
    } else {
      uploadSuccess();
    }
  };

  const realms = uniqBy(
    [
      get(assessment, 'data.realm'),
      ...get(assessment, 'data.realm.realms', []).sort((a, b) => {
        const A = get(a, 'name');
        const B = get(b, 'name');
        return A === B ? 0 : A > B ? 1 : -1;
      }),
    ],
    'id'
  );

  return <>
    <PhotosGrid>
      <ImageGrid
        uploadPhotos={uploadPhotos}
        download={download}
        dlStats={dlStats}
        folders={folders}
        includeSearch={true}
        loading={loading}
        update={updateAll}
        userCanEdit={userCanEdit || assessment.loading}
        realms={realms}
        supersets={supersets}
        assessment={assessment}
      />
    </PhotosGrid>

    <Dialog maxWidth={false} open={uploadModal} onClose={() => setUploadModal(false)} classes={{ paper: classes.diagContainer }}>
      <DialogTitle classes={{ root: classes.diagTitle }}>
        <AppBar color="secondary" className={classes.appBar} position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setUploadModal(false)}
              aria-label="Close"
              size="large">
              <Icon>close</Icon>
            </IconButton>
            <Typography variant="h6" className={classes.appBarTitle}>
              Upload Photos
            </Typography>
          </Toolbar>
        </AppBar>
      </DialogTitle>
      <DialogContent>
        <DropArea
          ref={dropRef}
          accept={['image/*', 'video/*']}
          onFilesChange={onDropChange}
          files={uploadFiles}
          className={classes.dropZone}
          renderContent={() => <>Drop Files here. Or click here.</>}
          renderFile={(file, key) => <UploadImage key={key} file={file} location={location} assessment={assessment} actions={{ onDeleteFile, updateImage }} />}
        />
      </DialogContent>
      {uploadStats.isUploading && <LinearProgress color="secondary" variant="determinate" value={uploadStats.progress} className={classes.loader} />}
      <DialogActions classes={{ root: classes.diagButtonContainer }}>
        {uploadStats.isUploading && (
          <Button className={classes.diagButton} variant="contained" color="primary" onClick={cancelUpload} disabled={!uploadStats.isUploading}>
            <Icon style={{ marginRight: isMobile ? 0 : 5 }}>close</Icon>
            {isMobile ? '' : 'Cancel Upload'}
          </Button>
        )}
        {!uploadStats.isUploading && (
          <FormControl className={classes.locationSelect} variant='standard'>
            <InputLabel shrink={true}>System</InputLabel>
            <Select displayEmpty value={superset} onChange={onSupersetChange}>
              {[{ name: 'None', value: null }, ...supersets].map((l, i) => (
                <MenuItem key={i} value={get(l, 'id')}>
                  {get(l, 'name')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!uploadStats.isUploading && (
          <FormControl className={classes.locationSelect} variant='standard'>
            <InputLabel>Location</InputLabel>
            <Select value={location} onChange={onLocationChange}>
              <MenuItem>None</MenuItem>
              {realms
                .filter((r) => r)
                .map((l, i) => (
                  <MenuItem key={i} value={get(l, 'id')}>
                    {get(l, 'name')}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        {!uploadStats.isUploading && (
          <Button className={classes.diagButton} variant="contained" color="secondary" onClick={submitFiles} disabled={!(location && size(uploadFiles) && !uploadStats.isUploading)}>
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={alerts}
      autoHideDuration={6000}
      onClose={() => setAlerts()}
    >
      <Alert onClose={() => setAlerts()} severity={get(alerts, 'severity', 'success')}>
        {get(alerts, 'message', '')}
      </Alert>
    </Snackbar>

    <Confirm {...confirm} />
  </>;
}

export default ReportAssessmentPhotos;
